import { contratosFetch } from '@/pantallas/SucursalVirtual/Contratos/contratosFetch';
import {
  ArchivoContrato,
  ArchivoIndividual,
  EtiquetasFiltrosDetalleContrato,
} from '@/pantallas/SucursalVirtual/Contratos/types';
import {
  CrearDetalleContrato,
  DetalleContrato as TypeDetalleContrato,
  DatosContrato,
  EditarDetalleContrato,
} from '@/pantallas/SucursalVirtual/Contratos/types';

class DetalleContrato {
  static async obtenerSoluciones() {
    try {
      const { data } = await contratosFetch({
        serviceClient: 'sucursal_virtual_cotizador',
        endpoint: 'getSoluciones',
      });
      const {
        MENSAJE_RESPUESTA,
        CODIGO_RESPUESTA,
        productsList: SOLUCIONES,
      } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return SOLUCIONES;
    } catch (error) {
      const mensajeError =
        error instanceof Error
          ? error.message
          : 'Ha ocurrido un error al obtener las soluciones';
      throw new Error(mensajeError);
    }
  }

  static async obtenerTipos() {
    try {
      const { data } = await contratosFetch({
        endpoint: 'getTipos',
      });
      const {
        MENSAJE_RESPUESTA,
        CODIGO_RESPUESTA,
        typesList: SOLUCIONES,
      } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return SOLUCIONES;
    } catch (error) {
      const mensajeError =
        error instanceof Error
          ? error.message
          : 'Ha ocurrido un error al obtener las soluciones';
      throw new Error(mensajeError);
    }
  }

  static async obtenerFiltrosDetalleContrato(
    filtrosDeseados: EtiquetasFiltrosDetalleContrato = [],
    contratoId: DatosContrato['contratoId'] = '0'
  ) {
    try {
      const { data } = await contratosFetch({
        endpoint: 'obtener_filtros_detalle_contratos',
        body: { filtrosDeseados, contratoId },
      });
      const {
        MENSAJE_RESPUESTA,
        CODIGO_RESPUESTA,
        ACTUAL_MODALIDAD_PLAZO,
        MODALIDADES,
        PLAZOS,
        AREAS,
      } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return {
        actualModalidadPlazo: ACTUAL_MODALIDAD_PLAZO,
        modalidades: MODALIDADES,
        plazos: PLAZOS,
        areas: AREAS,
      };
    } catch (error) {
      const mensajeError =
        error instanceof Error
          ? error.message
          : 'Ha ocurrido un error al obtener los filtros del detalle del contrato';
      throw new Error(mensajeError);
    }
  }

  static async crearDetalleContrato(
    detalleContrato: TypeDetalleContrato,
    usuarioId: string,
    clienteDefecto: string
  ) {
    const body: CrearDetalleContrato = {
      ...detalleContrato,
      usuarioId,
      clienteDefecto,
    };
    try {
      const { data } = await contratosFetch({
        endpoint: 'crear_detalle_contrato',
        body,
      });

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return {
        mensajeExito: MENSAJE_RESPUESTA,
      };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al crear el detalle del contrato';
      throw new Error(mensajeError);
    }
  }

  static async obtenerDetallesContratoPorId(
    contratoId: DatosContrato['contratoId']
  ) {
    try {
      const { data } = await contratosFetch({
        endpoint: 'obtener_detalles_contrato_por_id',
        body: { contratoId },
      });

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA, DETALLES_CONTRATO } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }

      return {
        detalleContratos: DETALLES_CONTRATO,
        mensajeExito: MENSAJE_RESPUESTA,
      };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al obtener los detalles del contrato';
      throw new Error(mensajeError);
    }
  }

  static async editarDetalleContrato(
    detalleContrato: TypeDetalleContrato,
    usuarioId: string,
    clienteDefecto: string
  ) {
    const body: EditarDetalleContrato = {
      ...detalleContrato,
      usuarioId,
      clienteDefecto,
    };
    try {
      const { data } = await contratosFetch({
        endpoint: 'editar_detalle_contrato',
        body,
      });

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }

      return {
        mensajeExito: MENSAJE_RESPUESTA,
      };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al editar el detalle del contrato';
      throw new Error(mensajeError);
    }
  }

  static async eliminarDetalleContrato(
    detalleId: string,
    usuarioId: string,
    clienteDefecto: string
  ) {
    try {
      const { data } = await contratosFetch({
        endpoint: 'eliminar_detalle_contrato',
        body: { detalleId, usuarioId, clienteDefecto },
      });

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }

      return {
        mensajeExito: MENSAJE_RESPUESTA,
      };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al eliminar el detalle del contrato';
      throw new Error(mensajeError);
    }
  }

  static async crearArchivo(
    archivos: ArchivoIndividual,
    contratoId: string | undefined,
    usuarioId: string,
    clienteDefecto: string,
    tipo: string // Añadir tipo como parámetro
  ) {
    try {
      const { data } = await contratosFetch({
        endpoint: 'crear_archivo',
        body: {
          contratoId,
          archivos,
          usuarioId,
          clienteDefecto,
          tipo, // Incluir tipo en el cuerpo de la solicitud
        },
      });

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }
      return { mensajeExito: MENSAJE_RESPUESTA };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al crear el archivo del contrato';
      throw new Error(mensajeError);
    }
  }

  static async obtenerArchivosPorId(contratoId: DatosContrato['contratoId']) {
    try {
      const { data } = await contratosFetch({
        endpoint: 'obtener_archivos_por_id',
        body: { contratoId },
      });

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA, ARCHIVOS } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }

      return {
        archivos: ARCHIVOS,
      };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al obtener los archivos del contrato';
      throw new Error(mensajeError);
    }
  }

  static async desactivaArchivo(
    archivoId: string,
    usuarioId: string,
    clienteDefecto: string
  ) {
    try {
      const { data } = await contratosFetch({
        endpoint: 'desactivar_archivo',
        body: { archivoId, usuarioId, clienteDefecto },
      });

      const { MENSAJE_RESPUESTA, CODIGO_RESPUESTA } = data;

      if (CODIGO_RESPUESTA !== '1') {
        throw new Error(MENSAJE_RESPUESTA);
      }

      return {
        mensajeExito: MENSAJE_RESPUESTA,
      };
    } catch (e) {
      const mensajeError =
        e instanceof Error
          ? e.message
          : 'Ha ocurrido un error al eliminar el archivo del contrato';
      throw new Error(mensajeError);
    }
  }
}

export default DetalleContrato;
